/**
 * Shift Log panel (#148) — the hand-over briefing surface.
 *
 * A left-rail tab (sibling to Queue + Layers) that reads the operator_actions
 * audit trail since the shared shift-start anchor (#147) and shows the
 * "what changed this shift" feed the transfer-of-command doctrine (§4.8) wants:
 * a per-verb rollup + a newest-first activity list (verb · target · time ·
 * operator). The "Generate Handover SITREP" button embeds this same summary into
 * a SITREP (reusing the engine — no template fork) and opens it in the viewer.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ShiftLogSummary, ShiftLogInclude, SitrepReport } from '@tsr/shared-types';
import { fetchShiftLog, createSitrep, CANADA_BBOX, type BboxQuery } from './api';
import { useShiftStart, clockLabel } from './lib/shiftStart';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';

/** Refresh cadence for the live feed (a handover is read on a stable view). */
const REFRESH_MS = 30_000;

function relTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function ShiftLogPanel({
  currentBbox,
  onOpenSitrep,
}: {
  currentBbox: BboxQuery | null;
  onOpenSitrep: (sitrep: SitrepReport) => void;
}): JSX.Element {
  const { shiftStart, reset } = useShiftStart();
  const [include, setInclude] = useState<ShiftLogInclude>('changes');
  const [log, setLog] = useState<ShiftLogSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [generating, setGenerating] = useState(false);

  const sinceIso = new Date(shiftStart).toISOString();

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(false);
      fetchShiftLog(sinceIso, { include }, signal)
        .then((r) => {
          setLog(r);
          setLoading(false);
        })
        .catch(() => {
          if (signal?.aborted) return;
          setError(true);
          setLoading(false);
        });
    },
    [sinceIso, include],
  );

  useEffect(() => {
    const ctl = new AbortController();
    load(ctl.signal);
    const id = window.setInterval(() => load(), REFRESH_MS);
    return () => {
      ctl.abort();
      window.clearInterval(id);
    };
  }, [load]);

  const generateHandover = (): void => {
    if (generating) return;
    setGenerating(true);
    void createSitrep({
      scope_type: 'region',
      scope_payload: currentBbox ?? CANADA_BBOX,
      hazard_focus: 'wildfire',
      shift_log_since: sinceIso,
      session_id: getSessionId(),
    })
      .then((sitrep) => {
        onOpenSitrep(sitrep);
        showToast('Handover SITREP generated with the shift-log summary.', { variant: 'success' });
      })
      .catch(() =>
        showToast('Could not generate the Handover SITREP. Please retry.', { variant: 'error' }),
      )
      .finally(() => setGenerating(false));
  };

  return (
    <div
      data-testid="shift-log-panel"
      className="flex h-full flex-col bg-slate-900/95 text-slate-100"
    >
      {/* Shift-start strip — shares the #147 anchor; reset re-anchors everywhere. */}
      <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-1.5 text-[11px] text-slate-400">
        <span>
          🕑 Shift since {clockLabel(shiftStart)} ·{' '}
          <span className="font-medium text-slate-200" data-testid="shift-log-count">
            {log ? log.count : '—'} {include === 'changes' ? 'changes' : 'actions'}
          </span>
        </span>
        <button
          type="button"
          data-testid="shift-log-reset"
          onClick={reset}
          title="Reset the shift-start marker to now"
          className="ml-auto rounded bg-slate-700/60 px-1.5 py-0.5 text-slate-300 hover:bg-slate-600"
        >
          reset
        </button>
      </div>

      {/* Controls: include filter + generate handover SITREP. */}
      <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-1.5 text-[11px]">
        <button
          type="button"
          data-testid="shift-log-include-toggle"
          onClick={() => setInclude((v) => (v === 'changes' ? 'all' : 'changes'))}
          className={`rounded px-1.5 py-0.5 ${
            include === 'all'
              ? 'bg-sky-600 text-white'
              : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600'
          }`}
          title="Toggle between state changes only and the full audit feed"
        >
          {include === 'changes' ? 'changes only' : 'all actions'}
        </button>
        <button
          type="button"
          data-testid="shift-log-handover-btn"
          disabled={generating}
          onClick={generateHandover}
          className="ml-auto rounded border border-emerald-500 bg-emerald-600/90 px-2 py-0.5 font-medium text-white hover:bg-emerald-600 disabled:cursor-wait disabled:opacity-50"
        >
          {generating ? 'Generating…' : 'Handover SITREP'}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Per-verb rollup — the grouped "what changed" summary. */}
        {log && log.by_verb.length > 0 && (
          <div
            data-testid="shift-log-by-verb"
            className="flex flex-wrap gap-1 border-b border-slate-800 px-3 py-2"
          >
            {log.by_verb.map((v) => (
              <span
                key={v.action_type}
                className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300"
                title={v.action_type}
              >
                {v.verb_label} <span className="font-semibold text-slate-100">×{v.count}</span>
              </span>
            ))}
          </div>
        )}

        {/* Chronological feed. */}
        {error ? (
          <p className="px-3 py-6 text-center text-[11px] text-rose-300">
            Could not load the shift log.{' '}
            <button type="button" onClick={() => load()} className="underline">
              retry
            </button>
          </p>
        ) : loading && !log ? (
          <p className="px-3 py-6 text-center text-[11px] text-slate-500">Loading shift log…</p>
        ) : log && log.entries.length === 0 ? (
          <p className="px-3 py-6 text-center text-[11px] text-slate-500">
            No {include === 'changes' ? 'changes' : 'actions'} recorded since shift start.
          </p>
        ) : (
          <ul data-testid="shift-log-entries">
            {log?.entries.map((e) => (
              <li
                key={e.id}
                data-testid="shift-log-entry"
                className="border-b border-slate-800/60 px-3 py-1.5 text-[11px]"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-slate-200">{e.verb_label}</span>
                  {e.target && <span className="truncate text-slate-400">· {e.target}</span>}
                  <span className="ml-auto shrink-0 text-[10px] text-slate-500">
                    {relTime(e.timestamp)}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500">{e.operator_id}</div>
              </li>
            ))}
          </ul>
        )}
        {log?.truncated && (
          <p className="px-3 py-2 text-center text-[10px] text-slate-500">
            Showing the most recent {log.count} — older actions truncated.
          </p>
        )}
      </div>
    </div>
  );
}
